import React from 'react';

import { Accordion } from 'components/Accordion';
import { AuthorCard } from 'components/AuthorCard';
import { ContentTimestampWithSource } from 'components/ContentTimestampWithSource';
import { getDisplayedAndExpandableAuthors } from 'lib/getDisplayedAndExpandableAuthors';
import cx from 'classnames';
import Breakpoints from 'lib/Breakpoints';
import styles from './styles.module.scss';

/**
 * ExpandableByline component renders a section with a timestamp and author cards.
 * If there are more authors than can be displayed, it shows a button to expand and view them.
 * @param {object} props - The component props.
 * @param {Array} props.authors - The list of authors.
 * @param {string} props.createdAt - The date the article was created.
 * @param {string} [props.orgName] - The name of the organization.
 * @param {string} [props.orgUrl] - The URL of the organization.
 * @param {string} props.publishedAt - The date the article was published.
 * @param {import('lib/graphqlTypeDefs.mjs').EmbeddedOrganization} [props.source] - The source organization.
 *  @param {string} [props.className] - Optional CSS class name.
 * @returns {React.ReactElement|null} The rendered component or null if no authors are provided.
 */
export function ExpandableByline(
  {
    authors: authorsProp = [],
    className,
    createdAt,
    orgName,
    orgUrl,
    publishedAt,
    source,
  },
) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(true);
  // Filter authors to only include those with authorType 'author' not 'contributor'
  const authors = authorsProp.filter((author) => author?.authorType === 'author');

  React.useEffect(() => {
    setIsMobile(Breakpoints.isS());
  }, []);

  const paidAuthor = authors.filter((author) => author?.person?.name.toLowerCase().includes('paid for by'));

  const {
    displayedAuthors,
    expandableAuthors,
  } = getDisplayedAndExpandableAuthors(
    authors,
    isMobile,
    paidAuthor[0],
  );

  /**
   * @param {object} author - The author object.
   */
  function renderAuthorCard(author) {
    if (!author?.person?.name) return null;

    return (
      <AuthorCard
        key={author.person.name}
        name={author.person.name}
        linkURL={author.person.url?.primary}
        imageURL={author.person.primaryImage?.url?.primary}
        jobTitle={author.person.jobTitle[0]}
      />
    );
  }

  return (
    <section
      className={cx(className, styles.container)}
      data-testid="expandable-byline"
      data-activity-map="inline-byline-article-top"
    >
      <ContentTimestampWithSource
        dateCreated={createdAt}
        datePublished={publishedAt}
        orgName={orgName}
        orgUrl={orgUrl}
        showCreatedDate={createdAt === publishedAt}
        source={source}
        testId="expandable-byline__timestamp"
      />
      { authors.length > 0 && (
        <div className={styles.displayedAuthors}>
          { displayedAuthors.map(renderAuthorCard) }
        </div>
      )}
      { expandableAuthors.length > 0 && (
        <Accordion
          title={isOpen ? 'collapse' : `+ ${expandableAuthors.length} more`}
          openDirection="up"
          isOpen={isOpen}
          onToggle={() => setIsOpen((prev) => !prev)}
          additionalStyles={{
            accordion: styles.accordion,
            accordion__header: styles.showMoreButton,
            accordion__content: styles.expandableAuthors,
          }}
        >
          <div className={styles.list}>
            { expandableAuthors.map(renderAuthorCard) }
          </div>
        </Accordion>
      )}
    </section>
  );
}
